import { useEffect, useState, type FormEvent } from "react";

import { PolyfutureApiClient } from "./api.js";
import type { BookRecord, CreateBookInput, OrganizationRecord, TodayProjection } from "./types.js";

export function BooksCatalogView({
  projection,
  onOpenBook,
  onCreateBook,
}: {
  projection: TodayProjection;
  onOpenBook: (bookId: string) => void;
  onCreateBook: () => void;
}) {
  return (
    <div className="books-catalog-view">
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">Your investments</p>
          <h1>Portfolios</h1>
          <p>
            Keep your accounts or strategies together. Open a portfolio to see its holdings, explore
            what could change, or review its alerts.
          </p>
        </div>
        {projection.permissions.canCreateBook ? (
          <button className="button button-primary" type="button" onClick={onCreateBook}>
            + Create a portfolio
          </button>
        ) : null}
      </header>

      {projection.books.length === 0 ? (
        <section className="empty-state full catalog-first-use">
          <p className="eyebrow">FIRST USE</p>
          <h2>Start with your first portfolio</h2>
          <p>
            Give it a name, then add the investments you want to understand. Nothing is being
            monitored yet.
          </p>
          {projection.permissions.canCreateBook ? (
            <button className="button button-primary" type="button" onClick={onCreateBook}>
              Create the first portfolio
            </button>
          ) : (
            <p>Organization administrator access is required to create the first portfolio.</p>
          )}
        </section>
      ) : (
        <section className="book-catalog-grid" aria-label="Your portfolios">
          {projection.books.map((book) => (
            <article className="book-catalog-card" key={book.id}>
              <div className="book-catalog-card-heading">
                <span className={`state-dot state-${book.monitoringState}`} aria-hidden="true" />
                <div>
                  <p className="section-kicker">{book.profileDisplayName}</p>
                  <h2>{book.name}</h2>
                </div>
                <span className={`status-label status-label-${book.monitoringState}`}>
                  {book.monitoringState}
                </span>
              </div>
              <p>{book.description || "No description added."}</p>
              <dl className="catalog-facts">
                <div>
                  <dt>Holdings</dt>
                  <dd>{book.positionCount}</dd>
                </div>
                <div>
                  <dt>Active what-ifs</dt>
                  <dd>{book.activeScenarioCount}</dd>
                </div>
                <div>
                  <dt>Version</dt>
                  <dd>{book.currentBookVersionNo ?? "Draft"}</dd>
                </div>
                <div>
                  <dt>Monitoring</dt>
                  <dd>{book.monitoringBasis.replaceAll("_", " ")}</dd>
                </div>
              </dl>
              <p className="catalog-freshness">
                Last updated{" "}
                <time dateTime={book.freshness.asOf}>{formatAsOf(book.freshness.asOf)}</time>
              </p>
              {/* Its hit area covers the whole card, so clicking anywhere on it opens the portfolio. */}
              <button
                className="button book-catalog-open"
                type="button"
                aria-label={`Open ${book.name}`}
                onClick={() => onOpenBook(book.id)}
              >
                Open portfolio
              </button>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

export function CreateBookView({
  client,
  onCancel,
  onCreated,
}: {
  client: PolyfutureApiClient;
  onCancel: () => void;
  onCreated: (book: BookRecord) => void;
}) {
  const [organization, setOrganization] = useState<OrganizationRecord>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    void client
      .getOrganization()
      .then((record) => {
        if (active) setOrganization(record);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageFor(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [client]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (organization?.activeProfile === undefined) return;
    const input = createBookInputFromForm(form, organization);
    setSubmitting(true);
    setError(undefined);
    try {
      onCreated(await client.createBook(input));
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-book-view">
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">Portfolios / New portfolio</p>
          <h1>Create a portfolio</h1>
          <p>
            Give your portfolio a name. Next, add your holdings and explore what could affect them.
          </p>
        </div>
      </header>

      {error === undefined ? null : (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      <form className="create-book-form" onSubmit={submit}>
        <fieldset disabled={loading || submitting || organization?.activeProfile === undefined}>
          <legend>Portfolio details</legend>
          <label>
            <span>Name</span>
            <input
              name="name"
              required
              maxLength={300}
              autoComplete="off"
              placeholder="Retirement account or Growth strategy"
            />
          </label>
          <label>
            <span>Description (optional)</span>
            <textarea name="description" rows={4} maxLength={4000} />
          </label>
          <details className="advanced-options">
            <summary>Portfolio settings (optional)</summary>
            <div className="form-grid">
              <div className="locked-field">
                <span>Portfolio kind</span>
                <strong>{organization?.activeProfile?.bookKind ?? "Unavailable"}</strong>
                <small>Set by the organization profile.</small>
              </div>
              <div className="locked-field">
                <span>Investor profile</span>
                <strong>{organization?.activeProfile?.displayName ?? "Unavailable"}</strong>
                <small>
                  {organization?.activeProfile === undefined
                    ? "No active organization profile is published."
                    : `${organization.activeProfile.version} · locked for this organization`}
                </small>
              </div>
            </div>
            <label>
              <span>Maximum active what-ifs</span>
              <input
                name="maxActiveScenarios"
                type="number"
                min={1}
                max={10000}
                defaultValue={12}
              />
            </label>
            <label className="checkbox-row">
              <input name="autoCoverage" type="checkbox" defaultChecked />
              <span>Suggest what-ifs after I add my holdings</span>
            </label>
            <p>
              Suggestions can run after holdings are saved. Monitoring starts separately when you
              turn it on.
            </p>
          </details>
        </fieldset>
        {loading ? <p role="status">Loading approved profile catalog…</p> : null}
        {!loading && organization?.activeProfile === undefined ? (
          <p role="alert">
            No active organization profile is available. Publish one before creating a portfolio.
          </p>
        ) : null}
        <div className="form-actions">
          <button className="button" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            className="button button-primary"
            type="submit"
            disabled={loading || submitting || organization?.activeProfile === undefined}
          >
            {submitting ? "Creating…" : "Create portfolio"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function createBookInputFromForm(
  form: FormData,
  organization: OrganizationRecord,
): CreateBookInput {
  const profile = organization.activeProfile;
  if (profile === undefined) {
    throw new Error("an active organization profile is required");
  }
  const rawMaximum = String(form.get("maxActiveScenarios") ?? "").trim();
  return {
    name: String(form.get("name") ?? ""),
    description: String(form.get("description") ?? ""),
    kind: profile.bookKind,
    profileKey: profile.key,
    profileVersion: profile.version,
    archetypeSetKey: "core.targeted.v1",
    autoCoverage: form.get("autoCoverage") === "on",
    ...(rawMaximum === "" ? {} : { maxActiveScenarios: Number(rawMaximum) }),
  };
}

export function PositionSourceChooser({
  onChooseFile,
  onChooseManual,
  onChooseSftp,
}: {
  onChooseFile: () => void;
  onChooseManual: () => void;
  onChooseSftp: () => void;
}) {
  return (
    <div className="source-chooser-view">
      <header className="page-heading-row">
        <div>
          <p className="eyebrow">Data / Position source</p>
          <h1>Choose how positions enter the portfolio</h1>
          <p>
            Every supported path ends in the same staged preview, validation, identity review, and
            immutable publication boundary.
          </p>
        </div>
      </header>
      <section className="source-option-grid" aria-label="Position source options">
        <SourceOption
          title="File upload"
          state="Available"
          body="Hash a CSV or XLSX locally, upload it to quarantine, and review the staged diff."
          action="Open file intake"
          onAction={onChooseFile}
        />
        <SourceOption
          title="Managed SFTP"
          state="Available to administrators"
          body="Use an encrypted, rights-bound source connection with capability tests and scheduled sync."
          action="Configure SFTP"
          onAction={onChooseSftp}
        />
        <SourceOption
          title="Controlled manual entry"
          state="Available"
          body="Manage individual governed records from the Exposures workspace. Changes create a new portfolio version."
          action="Open exposures"
          onAction={onChooseManual}
        />
        <SourceOption
          title="Brokerage connection"
          state="Not connected"
          body="No brokerage adapter has passed the rights, identity, and operational qualification gate."
        />
        <SourceOption
          title="Screenshot extraction"
          state="Not connected"
          body="Screenshots are not accepted as authoritative position records in the current product."
        />
      </section>
    </div>
  );
}

function SourceOption({
  title,
  state,
  body,
  action,
  onAction,
}: {
  title: string;
  state: string;
  body: string;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <article className="source-option">
      <span>{state}</span>
      <h2>{title}</h2>
      <p>{body}</p>
      {action === undefined || onAction === undefined ? null : (
        <button className="button" type="button" onClick={onAction}>
          {action}
        </button>
      )}
    </article>
  );
}

function formatAsOf(value: string): string {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(value));
}

function messageFor(caught: unknown): string {
  return caught instanceof Error ? caught.message : "The request could not be completed.";
}
